import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  Paperclip,
  UploadCloud,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";

type StatusValidade = "regular" | "a_vencer" | "vencido" | "sem_validade";

type Periodicidade =
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

type OperacionalKey =
  | "ok"
  | "pendente_anexo"
  | "vencida"
  | "em_renovacao"
  | "aguardando_validacao"
  | "pendente"
  | "nao_exigido";

type RastreabilidadeStatus =
  | "vigente"
  | "substituido"
  | "vencido"
  | "aguardando_validacao"
  | "cancelado";

type PortalResumo = {
  totalExigem: number;
  anexadosAtualizados: number;
  pendenteAnexo: number;
  vencidos: number;
  emRenovacao: number;
  aguardandoValidacao: number;
};

type OperacionalVisual = {
  key: OperacionalKey;
  label: string;
  background: string;
  color: string;
  border: string;
};

type CertidaoItem = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  empresa: string;
  cnpj: string;
  dataEmissao: string | null;
  dataValidade: string | null;
  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;
  areaResponsavel: string;
  responsavelNome?: string | null;
  statusValidade: StatusValidade;
  anexoDisponivel: boolean;
  s3Key?: string | null;
  observacao?: string | null;
  versao: number;
  vigente: boolean;
  obsoleto: boolean;
  documentoSubstituidoId?: number | null;
  dataSubstituicao?: string | null;
  portais: PortalResumo;
};

type DocumentoVersao = {
  id: number;
  documentoId: number;
  versao: number;
  arquivoNome: string;
  s3Key: string;
  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload: string;
  responsavelUpload: string;
  vigente: boolean;
  obsoleto: boolean;
  observacao?: string | null;
};

type DocumentoRastreabilidade = DocumentoVersao & {
  statusRastreabilidade: RastreabilidadeStatus;
  titulo: string;
  descricaoStatus: string;
};

const BASE_PATH = "/governanca-qualidade-e-compliance/certidoes-e-documentos";
const DEPARTMENT_COLOR = "#2f3f59";

const mockCertidoes: CertidaoItem[] = [
  {
    id: 1,
    nome: "Certidão Negativa de Débitos Federais",
    categoria: "Fiscal",
    orgaoEmissor: "Receita Federal / PGFN",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "10/04/2026",
    dataValidade: "07/10/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "semestral",
    areaResponsavel: "Financeiro",
    responsavelNome: "Contabilidade / Financeiro",
    statusValidade: "regular",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/cnd-federal-2026.pdf",
    observacao: null,
    versao: 2,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 5,
      anexadosAtualizados: 5,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 2,
    nome: "Certificado de Regularidade do FGTS",
    categoria: "Trabalhista",
    orgaoEmissor: "Caixa Econômica Federal",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "25/04/2026",
    dataValidade: "24/05/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "mensal",
    areaResponsavel: "Departamento Pessoal",
    responsavelNome: "DP / Contabilidade",
    statusValidade: "a_vencer",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/crf-fgts-2026-04.pdf",
    observacao: "Renovação mensal deve ser acompanhada preventivamente.",
    versao: 1,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 4,
      anexadosAtualizados: 1,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 3,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 3,
    nome: "Certidão Municipal de Niterói",
    categoria: "Fiscal municipal",
    orgaoEmissor: "Prefeitura de Niterói",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: null,
    dataValidade: null,
    tipoValidade: "com_vencimento",
    periodicidade: "sob_demanda",
    areaResponsavel: "Financeiro",
    responsavelNome: "Financeiro",
    statusValidade: "sem_validade",
    anexoDisponivel: false,
    s3Key: null,
    observacao: "Documento ainda não vinculado ao repositório.",
    versao: 0,
    vigente: false,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 3,
      anexadosAtualizados: 0,
      pendenteAnexo: 3,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 4,
    nome: "Certidão Trabalhista / CNDT",
    categoria: "Trabalhista",
    orgaoEmissor: "TST",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "02/01/2026",
    dataValidade: "01/07/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "semestral",
    areaResponsavel: "Departamento Pessoal",
    responsavelNome: "DP / Governança",
    statusValidade: "regular",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/cndt-2026.pdf",
    observacao: "Aguardando validação interna do arquivo vigente.",
    versao: 1,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 4,
      anexadosAtualizados: 2,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 2,
    },
  },
  {
    id: 5,
    nome: "Certidão Estadual",
    categoria: "Fiscal estadual",
    orgaoEmissor: "SEFAZ",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "01/12/2025",
    dataValidade: "01/03/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "sob_demanda",
    areaResponsavel: "Financeiro",
    responsavelNome: "Financeiro / Contabilidade",
    statusValidade: "vencido",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/certidao-estadual-2025.pdf",
    observacao: "Documento vencido. Aguardando nova emissão.",
    versao: 1,
    vigente: false,
    obsoleto: true,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 3,
      anexadosAtualizados: 1,
      pendenteAnexo: 0,
      vencidos: 2,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 6,
    nome: "Contrato Social",
    categoria: "Societário",
    orgaoEmissor: "Junta Comercial",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "01/03/2024",
    dataValidade: null,
    tipoValidade: "sem_vencimento",
    periodicidade: "sem_validade",
    areaResponsavel: "Diretoria / Administrativo",
    responsavelNome: "Diretoria",
    statusValidade: "sem_validade",
    anexoDisponivel: true,
    s3Key: "governanca/documentos/contrato-social.pdf",
    observacao:
      "Documento permanente. Revisar apenas em caso de alteração contratual.",
    versao: 3,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: 5,
    dataSubstituicao: "01/03/2024",
    portais: {
      totalExigem: 6,
      anexadosAtualizados: 6,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
];

const mockVersoes: DocumentoVersao[] = [
  {
    id: 1,
    documentoId: 1,
    versao: 1,
    arquivoNome: "cnd-federal-2025.pdf",
    s3Key: "governanca/certidoes/cnd-federal-2025.pdf",
    dataEmissao: "10/10/2025",
    dataValidade: "08/04/2026",
    dataUpload: "10/10/2025 14:20",
    responsavelUpload: "Financeiro",
    vigente: false,
    obsoleto: true,
    observacao: "Substituída por nova emissão.",
  },
  {
    id: 2,
    documentoId: 1,
    versao: 2,
    arquivoNome: "cnd-federal-2026.pdf",
    s3Key: "governanca/certidoes/cnd-federal-2026.pdf",
    dataEmissao: "10/04/2026",
    dataValidade: "07/10/2026",
    dataUpload: "10/04/2026 16:42",
    responsavelUpload: "Financeiro",
    vigente: true,
    obsoleto: false,
    observacao: "Versão vigente.",
  },
  {
    id: 3,
    documentoId: 2,
    versao: 1,
    arquivoNome: "crf-fgts-2026-04.pdf",
    s3Key: "governanca/certidoes/crf-fgts-2026-04.pdf",
    dataEmissao: "25/04/2026",
    dataValidade: "24/05/2026",
    dataUpload: "25/04/2026 11:10",
    responsavelUpload: "Departamento Pessoal",
    vigente: true,
    obsoleto: false,
    observacao: "Renovação mensal.",
  },
  {
    id: 4,
    documentoId: 5,
    versao: 1,
    arquivoNome: "certidao-estadual-2025.pdf",
    s3Key: "governanca/certidoes/certidao-estadual-2025.pdf",
    dataEmissao: "01/12/2025",
    dataValidade: "01/03/2026",
    dataUpload: "01/12/2025 09:30",
    responsavelUpload: "Financeiro",
    vigente: false,
    obsoleto: true,
    observacao: "Documento vencido. Aguardando nova emissão.",
  },
  {
    id: 5,
    documentoId: 6,
    versao: 1,
    arquivoNome: "contrato-social-abertura.pdf",
    s3Key: "governanca/documentos/contrato-social-abertura.pdf",
    dataEmissao: "01/03/2024",
    dataValidade: null,
    dataUpload: "01/03/2024 10:10",
    responsavelUpload: "Diretoria",
    vigente: false,
    obsoleto: true,
    observacao: "Substituído por alteração contratual posterior.",
  },
  {
    id: 6,
    documentoId: 6,
    versao: 2,
    arquivoNome: "alteracao-contratual-2025.pdf",
    s3Key: "governanca/documentos/alteracao-contratual-2025.pdf",
    dataEmissao: "12/08/2025",
    dataValidade: null,
    dataUpload: "12/08/2025 15:30",
    responsavelUpload: "Diretoria",
    vigente: false,
    obsoleto: true,
    observacao: "Substituído por consolidação contratual.",
  },
  {
    id: 7,
    documentoId: 6,
    versao: 3,
    arquivoNome: "contrato-social-consolidado.pdf",
    s3Key: "governanca/documentos/contrato-social-consolidado.pdf",
    dataEmissao: "01/03/2024",
    dataValidade: null,
    dataUpload: "20/01/2026 09:00",
    responsavelUpload: "Diretoria",
    vigente: true,
    obsoleto: false,
    observacao: "Documento societário vigente.",
  },
];

function parseBrazilianDate(value: string | null): Date | null {
  if (!value) return null;

  const parts = value.split("/").map((part) => Number(part));
  if (parts.length !== 3) return null;

  const [day, month, year] = parts;
  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDaysDiffFromToday(date: Date | null) {
  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatMesesDias(totalDias: number) {
  const diasAbsolutos = Math.abs(totalDias);

  if (diasAbsolutos < 30) return null;

  const meses = Math.floor(diasAbsolutos / 30);
  const dias = diasAbsolutos % 30;

  const mesesTexto = meses === 1 ? "1 mês" : `${meses} meses`;
  const diasTexto = dias === 1 ? "1 dia" : `${dias} dias`;

  if (dias === 0) return mesesTexto;

  return `${mesesTexto} e ${diasTexto}`;
}

function getPrazoOperacional(item: CertidaoItem) {
  if (item.tipoValidade === "sem_vencimento") return "Sem vencimento";
  if (item.tipoValidade === "sob_demanda") return "Sob demanda";
  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  const validade = parseBrazilianDate(item.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias === null) return "Validade não informada";

  const mesesDias = formatMesesDias(dias);
  const complemento = mesesDias ? ` (${mesesDias})` : "";

  if (dias > 1) return `Faltam ${dias} dias${complemento}`;
  if (dias === 1) return "Falta 1 dia";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Vencida há 1 dia";

  return `Vencida há ${Math.abs(dias)} dias${complemento}`;
}

function pluralPortal(qtd: number) {
  return qtd === 1 ? "portal" : "portais";
}

function getOperacionalVisual(portais: PortalResumo): OperacionalVisual {
  const total = portais.totalExigem || 0;
  const atualizados = portais.anexadosAtualizados || 0;

  if (total <= 0) {
    return {
      key: "nao_exigido",
      label: "Não exigido em portais",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }

  if (portais.vencidos > 0) {
    return {
      key: "vencida",
      label: `Vencida em ${portais.vencidos} ${pluralPortal(portais.vencidos)}`,
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    };
  }

  if (portais.pendenteAnexo > 0) {
    return {
      key: "pendente_anexo",
      label: `Pendente anexo em ${portais.pendenteAnexo} ${pluralPortal(
        portais.pendenteAnexo
      )}`,
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    };
  }

  if (portais.emRenovacao > 0) {
    return {
      key: "em_renovacao",
      label: `Renovação pendente em ${portais.emRenovacao} ${pluralPortal(
        portais.emRenovacao
      )}`,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
    };
  }

  if (portais.aguardandoValidacao > 0) {
    return {
      key: "aguardando_validacao",
      label: `Aguardando validação em ${
        portais.aguardandoValidacao
      } ${pluralPortal(portais.aguardandoValidacao)}`,
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "#ddd6fe",
    };
  }

  if (atualizados >= total) {
    return {
      key: "ok",
      label: `Atualizado em ${total} ${pluralPortal(total)}`,
      background: "#ecfdf3",
      color: "#166534",
      border: "#bbf7d0",
    };
  }

  const pendentesCalculados = Math.max(total - atualizados, 0);

  return {
    key: "pendente",
    label: `Pendente em ${pendentesCalculados} ${pluralPortal(
      pendentesCalculados
    )}`,
    background: "#fffbeb",
    color: "#92400e",
    border: "#fde68a",
  };
}

function getStatusValidadeLabel(status: StatusValidade) {
  switch (status) {
    case "regular":
      return "Regular";
    case "a_vencer":
      return "A vencer";
    case "vencido":
      return "Vencida";
    case "sem_validade":
      return "Sem validade";
    default:
      return "Indefinido";
  }
}

function getTipoValidadeLabel(tipo: TipoValidade) {
  switch (tipo) {
    case "com_vencimento":
      return "Com vencimento";
    case "sem_vencimento":
      return "Sem vencimento";
    case "sob_demanda":
      return "Sob demanda";
    case "revisao_periodica":
      return "Revisão periódica";
    default:
      return "Indefinido";
  }
}

function getPeriodicidadeLabel(periodicidade: Periodicidade) {
  switch (periodicidade) {
    case "mensal":
      return "Mensal";
    case "trimestral":
      return "Trimestral";
    case "semestral":
      return "Semestral";
    case "anual":
      return "Anual";
    case "sem_validade":
      return "Sem validade";
    case "sob_demanda":
      return "Sob demanda";
    default:
      return "Indefinida";
  }
}

function getStatusValidadeVisual(status: StatusValidade) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "a_vencer":
      return {
        background: "#fffbeb",
        color: "#92400e",
        border: "#fde68a",
        icon: <Clock3 size={12} />,
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "sem_validade":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <FileText size={12} />,
      };
  }
}

function getRastreabilidadeStatus(
  versao: DocumentoVersao,
  documento: CertidaoItem
): RastreabilidadeStatus {
  if (versao.vigente) {
    if (documento.portais.aguardandoValidacao > 0) {
      return "aguardando_validacao";
    }

    return "vigente";
  }

  if (documento.statusValidade === "vencido" && versao.obsoleto) {
    return "vencido";
  }

  if (versao.obsoleto) {
    return "substituido";
  }

  return "substituido";
}

function getRastreabilidadeLabel(status: RastreabilidadeStatus) {
  switch (status) {
    case "vigente":
      return "Documento vigente";
    case "substituido":
      return "Documento substituído";
    case "vencido":
      return "Documento vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "cancelado":
      return "Documento cancelado";
    default:
      return "Registro documental";
  }
}

function getRastreabilidadeDescricao(
  status: RastreabilidadeStatus,
  versao: DocumentoVersao
) {
  if (versao.observacao) return versao.observacao;

  switch (status) {
    case "vigente":
      return "Arquivo atualmente utilizado como referência oficial.";
    case "substituido":
      return "Arquivo mantido para controle e rastreabilidade documental.";
    case "vencido":
      return "Arquivo vencido, mantido para consulta e comprovação histórica.";
    case "aguardando_validacao":
      return "Arquivo anexado, aguardando validação operacional.";
    case "cancelado":
      return "Arquivo cancelado ou desconsiderado.";
    default:
      return "Registro mantido para rastreabilidade documental.";
  }
}

function getRastreabilidadeVisual(status: RastreabilidadeStatus) {
  switch (status) {
    case "vigente":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        dot: "#16a34a",
      };
    case "substituido":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        dot: "#64748b",
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        dot: "#dc2626",
      };
    case "aguardando_validacao":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
        dot: "#7c3aed",
      };
    case "cancelado":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        dot: "#ea580c",
      };
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        dot: "#64748b",
      };
  }
}

function formatNullableDate(value: string | null) {
  return value || "Não informado";
}

const DetailLine: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => {
  return (
    <div style={styles.detailLine}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value || "Não informado"}</span>
    </div>
  );
};

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const CertidaoDocumentoDetalhe: React.FC = () => {
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  const navigate = useNavigate();
  const { documentoId } = useParams<{ documentoId: string }>();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadObservacao, setUploadObservacao] = useState("");
  const [expandedPortalKey, setExpandedPortalKey] = useState<string | null>(
    null
  );

  const documento = useMemo(() => {
    const id = Number(documentoId);
    return mockCertidoes.find((item) => item.id === id) || null;
  }, [documentoId]);

  const versoes = useMemo(() => {
    if (!documento) return [];

    return mockVersoes
      .filter((item) => item.documentoId === documento.id)
      .sort((a, b) => b.versao - a.versao);
  }, [documento]);

  const rastreabilidade = useMemo<DocumentoRastreabilidade[]>(() => {
    if (!documento) return [];

    return versoes.map((versao) => {
      const statusRastreabilidade = getRastreabilidadeStatus(versao, documento);

      return {
        ...versao,
        statusRastreabilidade,
        titulo: getRastreabilidadeLabel(statusRastreabilidade),
        descricaoStatus: getRastreabilidadeDescricao(
          statusRastreabilidade,
          versao
        ),
      };
    });
  }, [documento, versoes]);

  if (!documento) {
    return (
      <div style={styles.page}>
        <TitleButtons fullBleedLine title="Documento não encontrado" />

        <div style={styles.content}>
          <section style={styles.emptyPanel}>
            <FileText size={28} color={DEPARTMENT_COLOR} />

            <div>
              <h2 style={styles.emptyTitle}>Documento não encontrado</h2>
              <p style={styles.emptyText}>
                O documento solicitado não existe ou não está disponível.
              </p>
            </div>

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate(`${BASE_PATH}/visao-geral`)}
            >
              Voltar para a listagem
            </button>
          </section>
        </div>
      </div>
    );
  }

  const statusValidadeVisual = getStatusValidadeVisual(
    documento.statusValidade
  );
  const operacional = getOperacionalVisual(documento.portais);
  const prazoOperacional = getPrazoOperacional(documento);
  const versaoVigente = versoes.find((item) => item.vigente);

  const portalClientesMock = {
    exigem: [
      {
        id: 1,
        nome: "Bravante / Marlin Navegação",
        portal: "Meritum",
        url: "https://portal.meritum.com.br",
      },
      {
        id: 2,
        nome: "Googlemarine",
        portal: "Portal do Fornecedor",
        url: "https://fornecedores.googlemarine.com.br",
      },
      {
        id: 3,
        nome: "OSM Thome",
        portal: "Vendor Portal",
        url: "https://vendor.osmthome.com",
      },
      {
        id: 4,
        nome: "Vision Marine",
        portal: "Supplier Center",
        url: "https://supplier.visionmarine.com",
      },
    ],

    atualizados: [
      {
        id: 1,
        nome: "Bravante / Marlin Navegação",
        portal: "Meritum",
        url: "https://portal.meritum.com.br",
      },
    ],

    pendenteAnexo: [
      {
        id: 2,
        nome: "Googlemarine",
        portal: "Portal do Fornecedor",
        url: "https://fornecedores.googlemarine.com.br",
      },
    ],

    vencidos: [],

    emRenovacao: [
      {
        id: 3,
        nome: "OSM Thome",
        portal: "Vendor Portal",
        url: "https://vendor.osmthome.com",
      },
      {
        id: 4,
        nome: "Vision Marine",
        portal: "Supplier Center",
        url: "https://supplier.visionmarine.com",
      },
    ],

    aguardandoValidacao: [
      {
        id: 5,
        nome: "Techmak UK",
        portal: "Compliance Portal",
        url: "https://compliance.techmak.co.uk",
      },
    ],
  };

  const portalRows = [
    {
      key: "exigem",
      label: "Portais que exigem",
      value: documento.portais.totalExigem,
      clientes: portalClientesMock.exigem,
      tone: "neutral",
    },
    {
      key: "atualizados",
      label: "Atualizados",
      value: documento.portais.anexadosAtualizados,
      clientes: portalClientesMock.atualizados,
      tone: "success",
    },
    {
      key: "pendenteAnexo",
      label: "Pendente anexo",
      value: documento.portais.pendenteAnexo,
      clientes: portalClientesMock.pendenteAnexo,
      tone: "warning",
    },
    {
      key: "vencidos",
      label: "Vencidos",
      value: documento.portais.vencidos,
      clientes: portalClientesMock.vencidos,
      tone: "danger",
    },
    {
      key: "emRenovacao",
      label: "Em renovação",
      value: documento.portais.emRenovacao,
      clientes: portalClientesMock.emRenovacao,
      tone: "info",
    },
    {
      key: "aguardandoValidacao",
      label: "Aguardando validação",
      value: documento.portais.aguardandoValidacao,
      clientes: portalClientesMock.aguardandoValidacao,
      tone: "purple",
    },
  ];

  const getPortalToneStyle = (tone: string): { color: string } => {
    switch (tone) {
      case "success":
        return { color: "#16a34a" };

      case "warning":
        return { color: "#d97706" };

      case "danger":
        return { color: "#dc2626" };

      case "info":
        return { color: "#2563eb" };

      case "purple":
        return { color: "#7c3aed" };

      case "neutral":
      default:
        return { color: "#475569" };
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    console.log("Arquivo selecionado:", selectedFile);
    console.log("Observação:", uploadObservacao);
  };

  return (
    <div style={styles.page}>
      <TitleButtons fullBleedLine title="Certidões e Documentos - Detalhe" />

      <div style={styles.content}>
        <section style={styles.backBar}>
          <button
            type="button"
            style={{
              ...styles.backButton,
              color: activeDepartmentColor,
            }}
            onClick={() => navigate(`${BASE_PATH}/visao-geral`)}
          >
            <ArrowLeft size={15} />
            <span>Voltar</span>
          </button>
        </section>

        <section style={styles.headerCard}>
          <div style={styles.headerIcon}>
            <FileText size={22} />
          </div>

          <div style={styles.headerContent}>
            <div style={styles.overline}>Documento corporativo</div>
            <h1 style={styles.title}>{documento.nome}</h1>

            <div style={styles.metaLine}>
              <span>{documento.categoria}</span>
              <span>•</span>
              <span>{documento.orgaoEmissor}</span>
              <span>•</span>
              <span>{documento.cnpj}</span>
            </div>
          </div>

          <div style={styles.statusColumn}></div>
        </section>

        <section style={styles.mainGrid}>
          <div style={styles.leftColumn}>
            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Anexo vigente</h2>
                <span style={styles.panelHint}>Arquivo oficial atual</span>
              </div>

              <div
                style={{
                  ...styles.fileCard,
                  ...(documento.anexoDisponivel
                    ? styles.fileCardActive
                    : styles.fileCardPending),
                }}
              >
                <div
                  style={{
                    ...styles.fileIcon,
                    ...(documento.anexoDisponivel
                      ? styles.fileIconActive
                      : styles.fileIconPending),
                  }}
                >
                  {documento.anexoDisponivel ? (
                    <CheckCircle2 size={18} />
                  ) : (
                    <Paperclip size={18} />
                  )}
                </div>

                <div style={styles.fileInfo}>
                  <div style={styles.fileName}>
                    {versaoVigente?.arquivoNome ||
                      documento.s3Key ||
                      "Nenhum arquivo vinculado"}
                  </div>

                  <div style={styles.fileMeta}>
                    {documento.anexoDisponivel
                      ? `Anexado em ${
                          versaoVigente?.dataUpload || "data não informada"
                        } por ${
                          versaoVigente?.responsavelUpload ||
                          "responsável não informado"
                        }`
                      : "Documento pendente de anexo"}
                  </div>
                </div>

                <div style={styles.fileActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.fileActionButton,
                      opacity: documento.anexoDisponivel ? 1 : 0.5,
                      cursor: documento.anexoDisponivel
                        ? "pointer"
                        : "not-allowed",
                    }}
                    disabled={!documento.anexoDisponivel}
                    onClick={() => {
                      console.log(
                        "Visualizar documento:",
                        versaoVigente?.s3Key || documento.s3Key
                      );
                    }}
                  >
                    <FileText size={13} />
                    Visualizar
                  </button>

                  <button
                    type="button"
                    style={{
                      ...styles.fileActionButton,
                      opacity: documento.anexoDisponivel ? 1 : 0.5,
                      cursor: documento.anexoDisponivel
                        ? "pointer"
                        : "not-allowed",
                    }}
                    disabled={!documento.anexoDisponivel}
                    onClick={() => {
                      console.log(
                        "Baixar documento:",
                        versaoVigente?.s3Key || documento.s3Key
                      );
                    }}
                  >
                    <Download size={13} />
                    Baixar
                  </button>

                  <span
                    style={{
                      ...styles.fileStatus,
                      ...(documento.anexoDisponivel
                        ? styles.fileStatusOk
                        : styles.fileStatusPending),
                    }}
                  >
                    {documento.anexoDisponivel
                      ? "Ativo atualmente"
                      : "Pendente"}
                  </span>
                </div>
              </div>

              <div style={styles.currentDocumentCompact}>
                <div style={styles.currentMainStatus}>
                  <div style={styles.currentStatusHeader}>
                    <span
                      style={{
                        ...styles.currentStatusBadge,
                        ...(documento.statusValidade === "regular"
                          ? styles.currentStatusRegular
                          : documento.statusValidade === "a_vencer"
                          ? styles.currentStatusWarning
                          : documento.statusValidade === "vencido"
                          ? styles.currentStatusDanger
                          : styles.currentStatusNeutral),
                      }}
                    >
                      {getStatusValidadeLabel(documento.statusValidade)}
                    </span>

                    <span style={styles.currentPeriodicityBadge}>
                      {getPeriodicidadeLabel(documento.periodicidade)}
                    </span>

                    <span style={styles.currentTypeBadge}>
                      {getTipoValidadeLabel(documento.tipoValidade)}
                    </span>
                  </div>

                  <div style={styles.currentStatusLine}>
                    <span>
                      Emissão:{" "}
                      <strong>
                        {formatNullableDate(
                          versaoVigente?.dataEmissao || documento.dataEmissao
                        )}
                      </strong>
                    </span>

                    <span>•</span>

                    <span>
                      Validade:{" "}
                      <strong>
                        {formatNullableDate(
                          versaoVigente?.dataValidade || documento.dataValidade
                        )}
                      </strong>
                    </span>

                    <span>•</span>

                    <span
                      style={{
                        ...styles.currentPrazoText,
                        ...(documento.statusValidade === "a_vencer" ||
                        documento.statusValidade === "vencido"
                          ? styles.currentPrazoTextDanger
                          : {}),
                      }}
                    >
                      {prazoOperacional}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.uploadArea}>
                <label style={styles.uploadLabel}>
                  <UploadCloud size={17} />
                  <span>Selecionar documento atualizado</span>

                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    style={styles.fileInput}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setSelectedFile(file);
                    }}
                  />
                </label>

                {selectedFile && (
                  <div style={styles.selectedFile}>
                    Selecionado: {selectedFile.name}
                  </div>
                )}

                <textarea
                  value={uploadObservacao}
                  onChange={(event) => setUploadObservacao(event.target.value)}
                  placeholder="Observação sobre o novo documento, substituição ou conferência..."
                  style={styles.textarea}
                />
              </div>
            </section>

            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Rastreabilidade documental</h2>
                <span style={styles.panelHint}>Cadeia de substituição</span>
              </div>

              <div style={styles.traceabilityListCompact}>
                {rastreabilidade.map((registro, index) => {
                  const visual = getRastreabilidadeVisual(
                    registro.statusRastreabilidade
                  );

                  return (
                    <div
                      key={registro.id}
                      style={{
                        ...styles.traceabilityRowCompact,
                        borderColor: visual.border,
                        background:
                          registro.statusRastreabilidade === "vigente"
                            ? "#fbfffd"
                            : registro.statusRastreabilidade === "vencido"
                            ? "#fffafa"
                            : registro.statusRastreabilidade ===
                              "aguardando_validacao"
                            ? "#fbfaff"
                            : "#ffffff",
                      }}
                    >
                      <div style={styles.traceabilityStatusColumn}>
                        <span
                          style={{
                            ...styles.traceabilityDotCompact,
                            background: visual.dot,
                          }}
                        />

                        <span
                          style={{
                            ...styles.traceabilityStatusCompact,
                            background: visual.background,
                            color: visual.color,
                            borderColor: visual.border,
                          }}
                        >
                          {registro.titulo}
                        </span>
                      </div>

                      <div style={styles.traceabilityFileColumn}>
                        <div style={styles.traceabilityFileTop}>
                          <span style={styles.traceabilityFileNameCompact}>
                            {registro.arquivoNome}
                          </span>
                        </div>

                        <div style={styles.traceabilityMetaCompact}>
                          <span>
                            Emissão: {formatNullableDate(registro.dataEmissao)}
                          </span>
                          <span>•</span>
                          <span>
                            Validade:{" "}
                            {formatNullableDate(registro.dataValidade)}
                          </span>
                          <span>•</span>
                          <span>Upload: {registro.dataUpload}</span>
                          <span>•</span>
                          <span>{registro.responsavelUpload}</span>
                        </div>

                        {registro.descricaoStatus && (
                          <div style={styles.traceabilityObservationCompact}>
                            {registro.descricaoStatus}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {rastreabilidade.length === 0 && (
                  <div style={styles.emptySmall}>
                    Nenhum registro documental vinculado.
                  </div>
                )}
              </div>
            </section>
          </div>

          <div style={styles.rightColumn}>
            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Informações principais</h2>
                <span style={styles.panelHint}>Resumo</span>
              </div>

              <div style={styles.detailList}>
                <DetailLine label="Empresa" value={documento.empresa} />
                <DetailLine
                  label="Área responsável"
                  value={documento.areaResponsavel}
                />
                <DetailLine
                  label="Responsável"
                  value={documento.responsavelNome}
                />
                <DetailLine label="Emissão" value={documento.dataEmissao} />
                <DetailLine label="Validade" value={documento.dataValidade} />
                <DetailLine label="Prazo" value={prazoOperacional} />
                <DetailLine
                  label="Tipo"
                  value={getTipoValidadeLabel(documento.tipoValidade)}
                />
                <DetailLine
                  label="Periodicidade"
                  value={getPeriodicidadeLabel(documento.periodicidade)}
                />
              </div>
            </section>

            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Situação nos portais</h2>
                <span style={styles.panelHint}>Consolidado</span>
              </div>

              <div style={styles.portalStatusBox}>
                <div style={styles.portalStatusBadges}>
                  <Badge background="#ecfdf3" color="#166534" border="#bbf7d0">
                    Atualizado em {documento.portais.anexadosAtualizados}{" "}
                    {pluralPortal(documento.portais.anexadosAtualizados)}
                  </Badge>

                  {operacional.key !== "ok" &&
                    operacional.key !== "nao_exigido" && (
                      <Badge
                        background={operacional.background}
                        color={operacional.color}
                        border={operacional.border}
                      >
                        {operacional.label}
                      </Badge>
                    )}

                  {operacional.key === "ok" && (
                    <Badge
                      background={operacional.background}
                      color={operacional.color}
                      border={operacional.border}
                    >
                      Tudo atualizado
                    </Badge>
                  )}
                </div>

                <div style={styles.portalStatusText}>
                  Consolidação dos portais externos que exigem este documento.
                </div>
              </div>

              <div style={styles.portalSummaryCompact}>
                {portalRows.map((row) => {
                  const isExpanded = expandedPortalKey === row.key;
                  const toneStyle = getPortalToneStyle(row.tone);

                  return (
                    <div key={row.key} style={styles.portalExpandableItem}>
                      <button
                        type="button"
                        style={{
                          ...styles.portalExpandableButton,
                          ...(isExpanded
                            ? styles.portalExpandableButtonActive
                            : {}),
                        }}
                        onClick={() =>
                          setExpandedPortalKey(isExpanded ? null : row.key)
                        }
                      >
                        <div style={styles.portalExpandableLeft}>
                          <span
                            style={{
                              ...styles.portalToneDot,
                              background: toneStyle.color,
                            }}
                          />

                          <span style={styles.portalExpandableLabel}>
                            {row.label}
                          </span>
                        </div>

                        <div style={styles.portalExpandableRight}>
                          <strong
                            style={{
                              ...styles.portalExpandableValue,
                              color: toneStyle.color,
                            }}
                          >
                            {row.value}
                          </strong>

                          <span
                            style={{
                              ...styles.portalChevron,
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              color: toneStyle.color,
                            }}
                          >
                            ▾
                          </span>
                        </div>
                      </button>

                      {isExpanded && (
                        <div style={styles.portalClientsBox}>
                          {row.clientes.length > 0 ? (
                            row.clientes.map((cliente) => (
                              <div
                                key={cliente.id}
                                style={styles.portalClientLine}
                              >
                                <div style={styles.portalClientInfo}>
                                  <span style={styles.portalClientName}>
                                    {cliente.nome}
                                  </span>

                                  <span style={styles.portalClientMeta}>
                                    {cliente.portal}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  style={styles.portalOpenButton}
                                  onClick={() => {
                                    window.open(
                                      cliente.url,
                                      "_blank",
                                      "noopener,noreferrer"
                                    );
                                  }}
                                >
                                  Abrir portal
                                </button>
                              </div>
                            ))
                          ) : (
                            <div style={styles.portalEmptyClients}>
                              Nenhum portal nesta situação.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  fileActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    minWidth: "fit-content",
  },

  fileActionButton: {
    height: 24,
    padding: "0 8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 11,
    fontWeight: 850,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  currentDocumentCompact: {
    margin: "0 12px 12px",
    border: "1px solid #e5e7eb",
    background: "#FFFFFF",
  },

  currentMainStatus: {
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  currentStatusHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },

  currentStatusBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  currentStatusRegular: {
    background: "#ecfdf3",
    color: "#166534",
    borderColor: "#bbf7d0",
  },

  currentStatusWarning: {
    background: "#fffbeb",
    color: "#92400e",
    borderColor: "#fde68a",
  },

  currentStatusDanger: {
    background: "#fef2f2",
    color: "#991b1b",
    borderColor: "#fecaca",
  },

  currentStatusNeutral: {
    background: "#f8fafc",
    color: "#475569",
    borderColor: "#cbd5e1",
  },

  currentPeriodicityBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  currentTypeBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid #dbe4ef",
    background: "#FFFFFF",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  currentStatusLine: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.35,
  },

  currentPrazoText: {
    fontSize: 12.2,
    fontWeight: 900,
    color: "#334155",
  },

  currentPrazoTextDanger: {
    color: "#b91c1c",
  },

  backBar: {
    minHeight: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    background: "transparent",
    border: "none",
    padding: "0 2px",
  },

  backButton: {
    height: 28,
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12.5,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  actionBar: {
    minHeight: 42,
    padding: "6px 8px",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  actionGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  primaryButton: {
    height: 31,
    padding: "0 11px",
    border: `1px solid ${DEPARTMENT_COLOR}`,
    background: DEPARTMENT_COLOR,
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },

  secondaryButton: {
    height: 31,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },

  headerCard: {
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
  },

  headerIcon: {
    width: 42,
    height: 42,
    background: "rgba(47, 63, 89, 0.08)",
    border: `1px solid ${DEPARTMENT_COLOR}`,
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  headerContent: {
    minWidth: 0,
  },

  overline: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  title: {
    margin: "2px 0 0",
    fontSize: 18,
    fontWeight: 900,
    color: "#111827",
    lineHeight: 1.2,
  },

  metaLine: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
  },

  statusColumn: {
    display: "flex",
    alignItems: "flex-end",
    flexDirection: "column",
    gap: 6,
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid",
  },

  noticeWarning: {
    background: "#fff8e8",
    borderColor: "#e7cf9a",
    color: "#6b4f1f",
  },

  noticeDanger: {
    background: "#fff1f1",
    borderColor: "#efb4b4",
    color: "#7f1d1d",
  },

  noticeIcon: {
    marginTop: 1,
    display: "inline-flex",
    flexShrink: 0,
  },

  noticeTitle: {
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 2,
  },

  noticeText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.25fr) minmax(360px, 0.75fr)",
    gap: 10,
    alignItems: "start",
  },

  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },

  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },

  panel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  panelHeader: {
    minHeight: 42,
    padding: "9px 12px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  panelTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 900,
    color: "#111827",
  },

  panelHint: {
    fontSize: 11,
    fontWeight: 800,
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  fileCard: {
    margin: 12,
    padding: 12,
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    display: "grid",
    gridTemplateColumns: "34px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 10,
  },

  fileIcon: {
    width: 34,
    height: 34,
    background: "#f2f5f8",
    border: "1px solid #e2e8f0",
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  fileInfo: {
    minWidth: 0,
  },

  fileName: {
    fontSize: 13,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  fileMeta: {
    marginTop: 2,
    fontSize: 11.5,
    fontWeight: 650,
    color: "#64748b",
  },

  fileStatus: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  fileStatusOk: {
    background: "#ecfdf3",
    color: "#166534",
    borderColor: "#bbf7d0",
  },

  fileStatusPending: {
    background: "#fff7ed",
    color: "#9a3412",
    borderColor: "#fed7aa",
  },

  currentDocumentGrid: {
    margin: "0 12px 12px",
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },

  currentInfoBox: {
    minHeight: 54,
    padding: "8px 10px",
    border: "1px solid #e5e7eb",
    background: "#fbfdff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 3,
  },

  currentInfoLabel: {
    fontSize: 10.5,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  currentInfoValue: {
    fontSize: 12.5,
    fontWeight: 900,
    color: "#111827",
  },

  uploadArea: {
    margin: "0 12px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  uploadLabel: {
    minHeight: 42,
    border: "1px dashed #94a3b8",
    background: "#f8fafc",
    color: DEPARTMENT_COLOR,
    fontSize: 12.5,
    fontWeight: 850,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
  },

  fileInput: {
    display: "none",
  },

  selectedFile: {
    fontSize: 11.8,
    fontWeight: 800,
    color: "#334155",
  },

  textarea: {
    width: "100%",
    minHeight: 62,
    resize: "vertical",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    padding: "8px 9px",
    fontSize: 12,
    fontWeight: 650,
    color: "#111827",
    outline: "none",
    fontFamily: "inherit",
  },

  traceabilityIntro: {
    padding: "10px 12px",
    borderBottom: "1px solid #eef2f5",
    background: "#ffffff",
    fontSize: 12,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.4,
  },

  traceabilityListCompact: {
    padding: 12,
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  traceabilityRowCompact: {
    minHeight: 48,
    padding: "8px 10px",
    border: "1px solid",
    display: "grid",
    gridTemplateColumns: "145px minmax(0, 1fr)",
    gap: 10,
    alignItems: "center",
  },

  traceabilityStatusColumn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },

  traceabilityDotCompact: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },

  traceabilityStatusCompact: {
    minHeight: 21,
    padding: "0 7px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 10.8,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  traceabilityFileColumn: {
    minWidth: 0,
  },

  traceabilityFileTop: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },

  traceabilityFileNameCompact: {
    fontSize: 12.3,
    fontWeight: 900,
    color: DEPARTMENT_COLOR,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  traceabilityCurrentTag: {
    minHeight: 18,
    padding: "0 6px",
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#64748b",
    fontSize: 10,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  traceabilityMetaCompact: {
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.25,
  },

  traceabilityObservationCompact: {
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 750,
    color: "#475569",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailList: {
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },

  detailLine: {
    minHeight: 34,
    borderBottom: "1px solid #eef2f5",
    display: "grid",
    gridTemplateColumns: "130px minmax(0, 1fr)",
    alignItems: "center",
    gap: 10,
  },

  detailLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  detailValue: {
    fontSize: 12.3,
    fontWeight: 750,
    color: "#111827",
    lineHeight: 1.25,
  },

  portalStatusBox: {
    padding: 12,
    borderBottom: "1px solid #eef2f5",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 7,
  },

  portalStatusText: {
    fontSize: 11.8,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  portalSummary: {
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  portalSummaryCompact: {
    padding: 12,
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  portalExpandableItem: {
    border: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  portalExpandableButton: {
    width: "100%",
    minHeight: 32,
    border: "none",
    background: "#ffffff",
    padding: "0 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    fontFamily: "inherit",
    outline: "none",
    boxShadow: "none",
  },

  portalExpandableButtonActive: {
    background: "#fbfdff",
  },

  portalExpandableLeft: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },

  portalToneDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
  },

  portalExpandableLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalExpandableRight: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    flexShrink: 0,
  },

  portalExpandableValue: {
    fontSize: 12.5,
    fontWeight: 950,
  },

  portalChevron: {
    width: 14,
    height: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1,
    transition: "transform 160ms ease",
  },

  portalClientsBox: {
    padding: "6px 8px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    background: "#fbfdff",
    borderTop: "1px solid #eef2f5",
  },

  portalClientLine: {
    minHeight: 31,
    padding: "5px 6px",
    border: "1px solid #eef2f5",
    background: "#ffffff",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 8,
  },

  portalClientInfo: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  portalClientName: {
    fontSize: 11.8,
    fontWeight: 850,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalClientMeta: {
    fontSize: 10.8,
    fontWeight: 700,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalOpenButton: {
    height: 23,
    padding: "0 7px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 10.8,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  portalEmptyClients: {
    padding: "7px 6px",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#64748b",
    textAlign: "center",
  },

  portalLine: {
    minHeight: 30,
    padding: "0 8px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 12,
    fontWeight: 750,
    color: "#334155",
  },

  governanceBox: {
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },

  governanceLine: {
    minHeight: 32,
    padding: "0 8px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 12,
    fontWeight: 750,
    color: "#334155",
  },

  governanceNote: {
    marginTop: 2,
    padding: "9px 10px",
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    fontSize: 11.6,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.4,
  },

  emptySmall: {
    padding: 12,
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textAlign: "center",
  },

  emptyPanel: {
    minHeight: 160,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 10,
    textAlign: "center",
    padding: 24,
  },

  emptyTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 900,
    color: "#111827",
  },

  emptyText: {
    margin: "4px 0 0",
    fontSize: 12.5,
    fontWeight: 650,
    color: "#64748b",
  },
};

export default CertidaoDocumentoDetalhe;
